import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { useTranslation } from '@shared/core';

export type CardSizeLabels = Readonly<{
  cocktail: string;
  luncheon: string;
  postcard4x6: string;
  magnet4x6: string;
  // Bare product names for picker rows, which show the size beside the name
  // ("Postcard  4×6") instead of folding it in.
  postcard: string;
  magnet: string;
  // The standalone-envelope row's whole title. Unlike every label above it names
  // the card the envelope FITS, not the envelope — an A7 envelope is 7.25 × 5.25
  // and holds a 5 × 7 card, so a bare size would claim the wrong object.
  envelopeFor5x7: string;
}>;

// Untranslated fallback for callers outside a React/i18n context (and tests).
const DEFAULT_LABELS: CardSizeLabels = {
  cocktail: 'Cocktail',
  luncheon: 'Luncheon',
  postcard4x6: 'Postcard 4x6',
  magnet4x6: 'Magnet 4x6',
  postcard: 'Postcard',
  magnet: 'Magnet',
  envelopeFor5x7: 'Envelopes for 5x7',
};

// Translated labels for named card sizes. The tq calls live here — directly in a
// hook with `useTranslation` in scope — so translation extraction can trace them;
// do not move them into `formatCardSizeName` (the extractor cannot follow
// function parameters).
export const useCardSizeLabels = (): CardSizeLabels => {
  const { tq } = useTranslation('stationery');
  return {
    cocktail: tq`Cocktail`,
    luncheon: tq`Luncheon`,
    postcard4x6: tq`Postcard 4x6`,
    magnet4x6: tq`Magnet 4x6`,
    postcard: tq`Postcard`,
    magnet: tq`Magnet`,
    envelopeFor5x7: tq`Envelopes for 5x7`,
  };
};

// 4x6/6x4 are the physical postcard dimensions; the label is orientation-agnostic
// because a 6x4 landscape card is the same physical postcard rotated (orientation
// has its own picker).
export const isPostcardCardSize = (widthInches: number, heightInches: number): boolean => (widthInches === 4 && heightInches === 6) || (widthInches === 6 && heightInches === 4);

// Categories Joy sells as postcards. Both thank-you and save-the-date ship at
// postcard dimensions with a hand-addressed mailing back and no envelope; every
// other category at 4x6/6x4 would be a template-author mistake, so postcard-ness
// is category AND size. Mirrors card_service's POSTCARD_CATEGORIES / isPostcard.
const POSTCARD_CATEGORIES: ReadonlySet<StationeryTemplateCategoryEnum> = new Set([StationeryTemplateCategoryEnum.thankYou, StationeryTemplateCategoryEnum.saveTheDate]);

export const isPostcardCategory = (category: StationeryTemplateCategoryEnum | undefined): boolean => !!category && POSTCARD_CATEGORIES.has(category);

// A template is a postcard when it is a postcard category at postcard dimensions.
export const isPostcard = (category: StationeryTemplateCategoryEnum | undefined, widthInches: number, heightInches: number): boolean =>
  isPostcardCategory(category) && isPostcardCardSize(widthInches, heightInches);

/**
 * Customer-facing name for a card size. Named sizes (napkins, postcards) read as
 * products; everything else falls back to measurements. Pass labels from
 * `useCardSizeLabels()` where a React context is available.
 */
export const formatCardSizeName = (
  category: StationeryTemplateCategoryEnum | undefined,
  widthInches: number,
  heightInches: number,
  labels: CardSizeLabels = DEFAULT_LABELS,
  productType?: string,
): string => {
  // No `envelope` branch here on purpose: the envelope product reaches the
  // picker through `formatProductSizeParts` and nothing else, so a branch here
  // would be unreachable. Add one if a surface ever calls this for an envelope —
  // the measurement fallback below would name the card it fits, not the envelope.
  // The magnet PRODUCT (MAGNET4X6) shares the saveTheDate postcard templates,
  // so the product — not category/size — decides its name.
  if (productType === 'magnet' && isPostcardCardSize(widthInches, heightInches)) {
    return labels.magnet4x6;
  }
  if (category === StationeryTemplateCategoryEnum.napkin) {
    if (widthInches === 4.75) return labels.cocktail;
    if (widthInches === 6.5) return labels.luncheon;
  }
  if (isPostcard(category, widthInches, heightInches)) {
    return labels.postcard4x6;
  }
  return `${widthInches} × ${heightInches} inches`;
};

export type ProductSizeTitleParts = Readonly<{ name: string; size?: string }>;

// Compact "5×7" — the picker rows set the size beside the product name, so the
// unit would only repeat down the list.
const formatCompactSize = (widthInches: number, heightInches: number): string => `${widthInches}×${heightInches}`;

/**
 * A picker row's title for a concrete product at a card size, split so the
 * row can set the size in a muted style beside the name: "Classic Paper" +
 * "5×7", "Postcard" + "4×6", "Magnet" + "4×6", "Napkins" + "Cocktail",
 * "Premium Digital" alone. Shared by the PDP picker and the customizer's
 * product switcher so one product never reads differently between the two.
 */
export const formatProductSizeParts = (
  category: StationeryTemplateCategoryEnum | undefined,
  widthInches: number,
  heightInches: number,
  labels: CardSizeLabels,
  product: Readonly<{ productType: string; cardTypeSKU?: unknown; translations: Readonly<{ displayName: string }> }>,
): ProductSizeTitleParts => {
  // Digital products have no size axis; their product name is the label.
  if (!product.cardTypeSKU) return { name: product.translations.displayName };
  // Envelopes carry a SKU but no size axis either — one A7 envelope, sold on its
  // own. The label names the card it fits, so it is the whole title: a size part
  // beside it would read as the envelope's own measurements, which it is not.
  if (product.productType === 'envelope') {
    return { name: labels.envelopeFor5x7 };
  }
  // The magnet PRODUCT (MAGNET4X6) shares the saveTheDate postcard templates,
  // so the product — not category/size — decides its name.
  if (product.productType === 'magnet' && isPostcardCardSize(widthInches, heightInches)) {
    return { name: labels.magnet, size: formatCompactSize(widthInches, heightInches) };
  }
  // Napkin sizes have names of their own ("Cocktail"), which read better than
  // their measurements beside the product.
  if (category === StationeryTemplateCategoryEnum.napkin) {
    if (widthInches === 4.75) return { name: product.translations.displayName, size: labels.cocktail };
    if (widthInches === 6.5) return { name: product.translations.displayName, size: labels.luncheon };
  }
  if (isPostcard(category, widthInches, heightInches)) {
    return { name: labels.postcard, size: formatCompactSize(widthInches, heightInches) };
  }
  return { name: product.translations.displayName, size: formatCompactSize(widthInches, heightInches) };
};
